import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useLenisRef } from '../context/ScrollContext';
import Hero from '../components/Hero/Hero';
import About from '../components/About/About';
import Education from '../components/Education/Education';
import Experience from '../components/Experience/Experience';
import ProjectsCarousel from '../components/Projects/ProjectsCarousel';
import SkillsSection from '../components/Skills/SkillsSection';
import CodeFeature from '../components/CodeFeature/CodeFeature';
import Connect from '../components/Connect/Connect';
import SectionCard from '../components/SectionCard/SectionCard';
import { scrollToId } from '../utils/scrollToSection';

const CARD_COUNT = 6;
const MOBILE_BREAKPOINT = 768;
const CARD_GAP_VH = 0.4; // must match --card-gap: 40dvh in index.css
// --card-sticky-top: calc(12px + 3.5rem + 36px) = 104px at 16px base font
const STICKY_TOP_PX = 104;
const CARD_BOTTOM_INSET_PX = 20; // must match --card-bottom-inset in index.css

export default function Home() {
  const location = useLocation();
  const lenisRef = useLenisRef();
  const excessRef = useRef(new Array(CARD_COUNT).fill(0));
  const [totalExcess, setTotalExcess] = useState(0);

  // useLayoutEffect fires after DOM commit but BEFORE the browser paints,
  // so the scroll position is set before the first visible frame — no flash.
  useLayoutEffect(() => {
    if (!location.state?.scrollTo) return;
    const target = location.state.scrollTo;
    window.history.replaceState({}, '');
    const lenis = lenisRef.current;
    if (!lenis) return;
    const el = document.getElementById(target);
    if (!el) return;
    const cardInner = el.closest('.card-inner');
    const scrollStart = cardInner ? parseFloat(cardInner.dataset.scrollStart) : NaN;
    if (isNaN(scrollStart)) { scrollToId(target); return; }
    // Lenis's Dimensions class uses a debounced ResizeObserver — its cached
    // limit may still reflect the previous page's height. Force an immediate
    // recompute so the target isn't clamped to the old (smaller) limit.
    lenis.resize();
    lenis.scrollTo(scrollStart, { immediate: true });
  }, [location.state, lenisRef]);

  useEffect(() => {
    if (location.state?.restoreScrollY != null) {
      const y = location.state.restoreScrollY;
      window.history.replaceState({}, '');
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

      let rafId;
      let attempts = 0;
      const tryScroll = () => {
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const lenis = lenisRef.current;
        if (maxScroll >= y || attempts > 60) {
          if (lenis) lenis.scrollTo(y, { immediate: true });
          else window.scrollTo(0, y);
        } else {
          attempts++;
          rafId = requestAnimationFrame(tryScroll);
        }
      };
      rafId = requestAnimationFrame(tryScroll);
      return () => cancelAnimationFrame(rafId);
    }
  }, [location.state, lenisRef]);

  const handleExcess = useCallback((index, px) => {
    if (excessRef.current[index] === px) return;
    excessRef.current[index] = px;
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      setTotalExcess(excessRef.current.reduce((s, v) => s + v, 0));
    }
  }, []);

  const getCardH = () => window.innerHeight - STICKY_TOP_PX - CARD_BOTTOM_INSET_PX;
  const getGapPx = () => CARD_GAP_VH * window.innerHeight;
  // One full card cycle: card height + spacer gap.
  const getScrollStep = () => getCardH() + getGapPx();

  // Compute per-card scrollStart: index*(cardH + gap) + cumulative excess before this card.
  const getScrollStart = (index) => {
    let cumulative = 0;
    for (let i = 0; i < index; i++) cumulative += excessRef.current[i];
    return index * getScrollStep() + cumulative;
  };

  return (
    <div className="home-page">
      {/*
        --extra-scroll is consumed by the mobile min-height calc in SectionCard.css.
        CSS handles the stickyTop subtraction; we just inject the total excess px.
      */}
      <div
        className="cards-stack"
        style={{ '--total-cards': CARD_COUNT, '--extra-scroll': `${totalExcess}px` }}
      >
        <SectionCard index={0} isLast={false} isWide scrollStart={getScrollStart(0)} scrollStep={getScrollStep()} onExcess={handleExcess}><Hero /></SectionCard>
        <div className="card-spacer" />
        <SectionCard index={1} isLast={false} scrollStart={getScrollStart(1)} scrollStep={getScrollStep()} onExcess={handleExcess}><About /></SectionCard>
        <div className="card-spacer" />
        <SectionCard index={2} isLast={false} scrollStart={getScrollStart(2)} scrollStep={getScrollStep()} onExcess={handleExcess}><Experience /></SectionCard>
        <div className="card-spacer" />
        <SectionCard index={3} isLast={false} scrollStart={getScrollStart(3)} scrollStep={getScrollStep()} onExcess={handleExcess}><Education /></SectionCard>
        <div className="card-spacer" />
        <SectionCard index={4} isLast={false} isWide scrollStart={getScrollStart(4)} scrollStep={getScrollStep()} onExcess={handleExcess}><ProjectsCarousel /></SectionCard>
        <div className="card-spacer" />
        <SectionCard index={5} isLast={true} scrollStart={getScrollStart(5)} scrollStep={getScrollStep()} onExcess={handleExcess}><SkillsSection /></SectionCard>
      </div>

      <div className="floor-rail">
        <CodeFeature />
        <Connect />
      </div>
    </div>
  );
}
